import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../../api";
import { Card } from "../../ui";
import { EditUserCard } from "./AdminUsers";

/**
 * Dedicated edit page for an admin user. Routed at
 * /admin/users/:userId/edit. Replaces the inline expand-below-the-list
 * pattern so editing doesn't push the table off-screen. The reusable
 * form lives in AdminUsers.tsx (EditUserCard); this wrapper handles
 * the route plumbing — load buildings, render the form, navigate back
 * on close.
 */

type Building = { id: string; name: string };

export function AdminUserEdit() {
  const { userId = "" } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState<Building[]>([]);

  useEffect(() => {
    void api
      .get<{ data: Building[] }>("/api/buildings")
      .then((r) => setBuildings(r.data))
      .catch(() => setBuildings([]));
  }, []);

  const goBack = () => navigate("/admin/users");

  return (
    <div>
      <div className="-mt-6 -mx-4 sm:-mx-6 lg:-mx-8 mb-6 bg-brand-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-5 pb-7">
          <Link
            to="/admin/users"
            className="inline-flex items-center gap-1 text-[12px] text-white/70 hover:text-white no-underline hover:no-underline mb-3"
          >
            <ArrowLeft size={13} /> All users
          </Link>
          <h1 className="text-white m-0">Edit user</h1>
        </div>
      </div>

      {buildings.length === 0 ? (
        <Card>
          <p className="text-ink-500 text-sm m-0">Loading…</p>
        </Card>
      ) : (
        <EditUserCard
          userId={userId}
          buildings={buildings}
          onClose={goBack}
          onChanged={() => {
            // List reloads on its own when the admin navigates back.
          }}
        />
      )}
    </div>
  );
}
